import type { TaskPayload, Status, Action, TaxonLite } from "@/types";
import { formatWeightKg } from "@/utils";

export default class Task {
  id: number;
  crop: number | null;
  garden: number | null;
  gardenName: string | null;
  taxon: TaxonLite | null;
  action: Action | null;
  actionDisplay: string;
  name: string | null;
  status: Status;
  statusDisplay: string;
  dateFrom: Date;
  dateTo: Date;
  startedAt: Date | null;
  doneAt: Date | null;
  plantCountRounded: number | null;
  seedWeight: number | null;
  // kg harvested within this task's window. Non-null only for harvest tasks
  // (action 40 EARLY_HARVEST / 41 HARVEST); informational, doesn't affect status.
  harvestedKg: number | null;
  // Watering dose for action 110 tasks (else null): depth (mm = L/m²) and surface to
  // cover (m²). Recommended before the task is done, actual applied after.
  waterDepthMm: number | null;
  waterAreaM2: number | null;

  constructor({
    id,
    crop,
    garden,
    garden_name: gardenName,
    taxon,
    action,
    action_display: actionDisplay,
    name,
    status,
    status_display: statusDisplay,
    date_from: dateFrom,
    date_to: dateTo,
    started_at: startedAt,
    done_at: doneAt,
    plant_count_rounded: plantCountRounded,
    seed_weight: seedWeight,
    harvested_kg: harvestedKg,
    water_depth_mm: waterDepthMm,
    water_area_m2: waterAreaM2,
  }: TaskPayload) {
    this.id = id;
    this.crop = crop;
    this.garden = garden ?? null;
    this.gardenName = gardenName ?? null;
    this.taxon = taxon;
    this.action = action;
    this.actionDisplay = actionDisplay;
    this.name = name;
    this.status = status;
    this.statusDisplay = statusDisplay;
    // Watering tasks are dated "today" with no end date. Treat a missing start as
    // now, and a missing end as the end of the start day so the task reads as "to do
    // now" (not a broken Invalid Date), then becomes overdue the next day.
    this.dateFrom = dateFrom ? new Date(Date.parse(dateFrom)) : new Date();
    this.dateTo = dateTo ? new Date(Date.parse(dateTo)) : new Date(this.dateFrom.getTime() + 86400000);
    this.startedAt = startedAt ? new Date(Date.parse(startedAt)) : null;
    this.doneAt = doneAt ? new Date(Date.parse(doneAt)) : null;
    this.plantCountRounded = plantCountRounded;
    this.seedWeight = seedWeight;
    this.harvestedKg = harvestedKg ?? null;
    this.waterDepthMm = waterDepthMm ?? null;
    this.waterAreaM2 = waterAreaM2 ?? null;
  }

  // Total water volume for a watering task (litres = mm × m²), null unless both the depth
  // and the surface are known. (Recommended before the task is done, actual after.)
  get waterVolumeL(): number | null {
    if (this.waterDepthMm == null || this.waterAreaM2 == null) return null;
    return this.waterDepthMm * this.waterAreaM2;
  }

  // A garden-level watering task (action 110, generated from the weather): no crop,
  // but a garden. Completing it logs a real watering instead of a plain status flip.
  get isWatering(): boolean {
    return this.action === 110 && this.crop == null && this.garden != null;
  }

  // A "buy seeds" task (action 10) for a known plant. Completing it offers to add the
  // matching packet to the seed library.
  get isBuySeeds(): boolean {
    return this.action === 10 && this.taxon != null;
  }

  // Crop tasks display the action; free-form tasks display their name. A watering
  // task reads "Arroser <nom du potager>" when the garden name is known.
  get title(): string {
    if (this.isWatering && this.gardenName) return `Arroser : ${this.gardenName}`;
    return this.name || this.actionDisplay;
  }

  // Folds the plant count / seed weight into the title when known:
  // "Acheter des plants" → "Acheter 10 plants", "Planter" → "Planter 10 plants",
  // "Acheter des graines" → "Acheter 5 g de graines".
  get displayTitle(): string {
    if (this.plantCountRounded != null) {
      const plants = `${this.plantCountRounded} plant${this.plantCountRounded > 1 ? "s" : ""}`;
      // Replace an existing "(des) plants" phrase, else append the count.
      return /(?:des\s+)?\bplants?\b/i.test(this.title)
        ? this.title.replace(/(?:des\s+)?\bplants?\b/i, plants)
        : `${this.title} ${plants}`;
    }
    // Only shown for seed purchases for now, not sowing tasks (action 10 = BUY_SEEDS).
    if (this.seedWeight != null && this.action === 10) {
      const weight = `${this.seedWeight.toLocaleString("fr-FR")} g`;
      // "Acheter des graines" → "Acheter 5 g de graines", else append the weight.
      return /(?:des\s+)?\bgraines\b/i.test(this.title)
        ? this.title.replace(/(?:des\s+)?\bgraines\b/i, `${weight} de graines`)
        : `${this.title} ${weight}`;
    }
    return this.title;
  }

  get isDone(): boolean {
    return this.status === "DONE";
  }

  get isInProgress(): boolean {
    return this.status === "IN_PROGRESS";
  }

  get isTodo(): boolean {
    return this.status === "TODO";
  }

  // A pending task whose ideal period has already passed.
  get isOverdue(): boolean {
    return this.status === "TODO" && new Date() > this.dateTo;
  }

  // A future task whose window opens within a month (vs. "later on").
  get isStartingSoon(): boolean {
    return (this.dateFrom.getTime() - Date.now()) / 86400000 <= 30;
  }

  // Harvested weight with the handiest unit ("1,2 kg" / "800 g"), null for
  // non-harvest tasks (where harvestedKg is null).
  get harvestedDisplay(): string | null {
    return this.harvestedKg == null ? null : formatWeightKg(this.harvestedKg);
  }
}
